import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
  Alert,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AccessTime, Event, CheckCircle } from "@mui/icons-material";
import { FormButtonGroup } from "../../form";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { isMobile } from "react-device-detect";
import React, { useEffect } from "react";

interface IRescheduleContentProps {
  loadingSlots: boolean;
  availableSlots: any[];
  availableDates: string[];
  slotsByDate: any;
  selectedDate: string;
  setSelectedDate: (date: string) => void;
  selectedSlot: string;
  setSelectedSlot: (slot: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
  currentSlot?: any; // Slot atual do appointment
  appointmentStatus?: string; // Status do appointment para identificar se é confirmação
  hideActions?: boolean;
  /** Em painéis embutidos (ex.: remarcação em lote), remove largura mínima e scroll só nos horários. */
  embedded?: boolean;
}

export function RescheduleContent({
  loadingSlots,
  availableSlots,
  availableDates,
  slotsByDate,
  selectedDate,
  setSelectedDate,
  selectedSlot,
  setSelectedSlot,
  onCancel,
  onConfirm,
  loading,
  currentSlot,
  appointmentStatus,
  hideActions = false,
  embedded = false,
}: IRescheduleContentProps) {
  // Verifica se é uma confirmação (status "proposed")
  const isConfirmation = appointmentStatus === "proposed";
  
  // Obtém a data do slot atual
  const currentSlotDate = currentSlot
    ? dayjs(currentSlot.start).format("YYYY-MM-DD")
    : null;
  
  // Verifica se o slot atual está na lista de slots disponíveis
  const currentSlotInAvailable = currentSlot
    ? availableSlots.some((slot: any) => slot.id === currentSlot.id)
    : false;
  
  // Seleciona automaticamente o slot atual se for confirmação e ainda não selecionou
  useEffect(() => {
    if (
      isConfirmation &&
      currentSlot &&
      !selectedSlot &&
      currentSlotInAvailable &&
      currentSlotDate
    ) {
      setSelectedSlot(currentSlot.id);
      if (!selectedDate) {
        setSelectedDate(currentSlotDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirmation, currentSlot?.id, currentSlotInAvailable, currentSlotDate]);
  return (
    <Stack
      spacing={2}
      sx={{
        minWidth: embedded || isMobile ? "100%" : 500,
        width: "100%",
        maxWidth: "100%",
        height: embedded ? "100%" : "auto",
        minHeight: embedded ? 0 : undefined,
      }}
      mt={embedded ? 0 : 1}
    >
      {loadingSlots ? (
        <Stack alignItems="center" py={6} spacing={2}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Carregando horários...
          </Typography>
        </Stack>
      ) : availableSlots.length === 0 ? (
        <Stack alignItems="center" py={4} spacing={2} sx={{ opacity: 0.7 }}>
          <Event sx={{ fontSize: 48, color: "text.disabled" }} />
          <Typography textAlign="center" color="text.secondary">
            Sem horários livres nos próximos 30 dias.
          </Typography>
        </Stack>
      ) : (
        <>
          {isConfirmation && currentSlot && (
            <Alert severity="info" icon={<CheckCircle />} sx={{ mb: 0 }}>
              <Typography variant="body2" fontWeight={500} gutterBottom>
                Horário Atual da Consulta:
              </Typography>
              <Typography variant="body2">
                {dayjs(currentSlot.start).format("DD/MM/YYYY [às] HH:mm")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                Você pode manter este horário ou selecionar um novo
              </Typography>
            </Alert>
          )}

          <Box mb={embedded ? 0 : 3} mt={embedded ? 0 : 1}>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="pt-br"
            >
              <DatePicker
                label="Data"
                value={selectedDate ? dayjs(selectedDate) : null}
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedDate(newValue.format("YYYY-MM-DD"));
                    const newDateKey = newValue.format("YYYY-MM-DD");
                    if (
                      currentSlot &&
                      currentSlotDate !== newDateKey &&
                      selectedSlot === currentSlot.id
                    ) {
                      setSelectedSlot("");
                    } else if (
                      currentSlot &&
                      currentSlotDate === newDateKey &&
                      !selectedSlot
                    ) {
                      setSelectedSlot(currentSlot.id);
                    }
                  }
                }}
                shouldDisableDate={(date) => {
                  return !availableDates.includes(date.format("YYYY-MM-DD"));
                }}
                disableHighlightToday={false}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
                minDate={dayjs()}
                maxDate={dayjs().add(30, "day")}
              />
            </LocalizationProvider>
          </Box>

          {selectedDate && slotsByDate[selectedDate] && (
            <Box
              sx={
                embedded
                  ? {
                      flex: 1,
                      minHeight: 96,
                      overflowY: "auto",
                      overflowX: "hidden",
                      pr: 0.5,
                      pb: 0.5,
                    }
                  : { maxHeight: "60vh", overflowY: "auto", pr: 1 }
              }
            >
              <Typography variant="subtitle2" color="text.secondary" mb={1.5}>
                Horários
              </Typography>
              <Grid container spacing={1}>
                {slotsByDate[selectedDate].map((slot: any) => {
                  const isCurrentSlot = currentSlot?.id === slot.id;
                  return (
                    <Grid item key={slot.id}>
                      <Chip
                        label={
                          isCurrentSlot && isConfirmation
                            ? `${dayjs(slot.start).format("HH:mm")} (Atual)`
                            : dayjs(slot.start).format("HH:mm")
                        }
                        onClick={() => setSelectedSlot(slot.id)}
                        color={
                          selectedSlot === slot.id
                            ? isCurrentSlot && isConfirmation
                              ? "success"
                              : "primary"
                            : "default"
                        }
                        variant={
                          selectedSlot === slot.id ? "filled" : "outlined"
                        }
                        icon={<AccessTime />}
                        sx={{
                          minWidth: isCurrentSlot && isConfirmation ? 120 : 80,
                          "& .MuiChip-label": { fontWeight: 500 },
                          ...(isCurrentSlot &&
                            isConfirmation &&
                            selectedSlot === slot.id && {
                              border: "2px solid",
                              borderColor: "success.main",
                            }),
                        }}
                        clickable
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </>
      )}

      {!hideActions && (
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          pt={2}
          borderTop={1}
          borderColor="divider"
          width="100%"
        >
          <FormButtonGroup
            onGoBackButtonClick={onCancel}
            onNextButtonClick={onConfirm}
            goBackButtonText="Voltar"
            nextButtonText={
              isConfirmation && currentSlot?.id === selectedSlot
                ? "Confirmar Horário"
                : "Confirmar Troca"
            }
            loading={loading}
            disabled={!selectedSlot}
          />
        </Stack>
      )}
    </Stack>
  );
}
